/**
 * Presets — named, data-only variants of families.
 *
 * A preset is pure JSON: { id, familyId, label, params }. It ships as a plain array so a
 * future "import preset JSON" via StorageProvider needs zero engine changes (§6 of spec).
 *
 * Prop presets and opening presets live here together; they're distinguished by `domain`.
 */

import type { ParamValues } from '@/model/params'

export interface Preset {
  id: string
  familyId: string
  domain: 'prop' | 'opening'
  label: string
  params: Partial<ParamValues>
}

// ---------------------------------------------------------------------------
// Prop presets
// ---------------------------------------------------------------------------

export const PROP_PRESETS: Preset[] = [
  // ── Bed ───────────────────────────────────────────────────────────────────
  { id: 'bed-single',  familyId: 'bed', domain: 'prop', label: 'Single bed',  params: { size: 'single' } },
  { id: 'bed-double',  familyId: 'bed', domain: 'prop', label: 'Double bed',  params: { size: 'double' } },
  { id: 'bed-queen',   familyId: 'bed', domain: 'prop', label: 'Queen bed',   params: { size: 'queen'  } },
  { id: 'bed-king',    familyId: 'bed', domain: 'prop', label: 'King bed',    params: { size: 'king'   } },
  { id: 'bed-bunk',    familyId: 'bed', domain: 'prop', label: 'Bunk bed',    params: { size: 'single', bunk: true } },

  // ── Sofa ──────────────────────────────────────────────────────────────────
  { id: 'sofa-2seat',  familyId: 'sofa', domain: 'prop', label: '2-seat sofa',  params: { shape: 'straight', seatLength: 5, depth: 3 } },
  { id: 'sofa-3seat',  familyId: 'sofa', domain: 'prop', label: '3-seat sofa',  params: { shape: 'straight', seatLength: 7, depth: 3 } },
  { id: 'sofa-L',      familyId: 'sofa', domain: 'prop', label: 'L-sofa',       params: { shape: 'L', seatLength: 8, depth: 3 } },
  { id: 'sofa-U',      familyId: 'sofa', domain: 'prop', label: 'U-sofa',       params: { shape: 'U', seatLength: 9, depth: 3 } },

  // ── Chair ─────────────────────────────────────────────────────────────────
  { id: 'chair-std',   familyId: 'chair', domain: 'prop', label: 'Chair',        params: { width: 1.8, depth: 1.8 } },
  { id: 'chair-arm',   familyId: 'chair', domain: 'prop', label: 'Armchair',     params: { width: 2.5, depth: 2.5 } },

  // ── Table ─────────────────────────────────────────────────────────────────
  { id: 'table-4',     familyId: 'table', domain: 'prop', label: 'Dining table (4)',  params: { width: 4, depth: 2.5 } },
  { id: 'table-6',     familyId: 'table', domain: 'prop', label: 'Dining table (6)',  params: { width: 6, depth: 3 } },

  // ── Wardrobe ──────────────────────────────────────────────────────────────
  { id: 'wardrobe-2d', familyId: 'wardrobe', domain: 'prop', label: 'Wardrobe 2-door', params: { doors: '2door', width: 4, depth: 2 } },
  { id: 'wardrobe-3d', familyId: 'wardrobe', domain: 'prop', label: 'Wardrobe 3-door', params: { doors: '3door', width: 6, depth: 2 } },

  // ── TV ────────────────────────────────────────────────────────────────────
  { id: 'tv-32',       familyId: 'tv', domain: 'prop', label: '32″ TV',          params: { diagonalIn: 32,  mount: 'stand' } },
  { id: 'tv-55',       familyId: 'tv', domain: 'prop', label: '55″ TV',          params: { diagonalIn: 55,  mount: 'stand' } },
  { id: 'tv-85',       familyId: 'tv', domain: 'prop', label: '85″ TV',          params: { diagonalIn: 85,  mount: 'wall'  } },
  { id: 'tv-proj',     familyId: 'tv', domain: 'prop', label: 'Projector+screen', params: { diagonalIn: 100, mount: 'projector' } },

  // ── Toilet ────────────────────────────────────────────────────────────────
  { id: 'toilet-std',  familyId: 'toilet', domain: 'prop', label: 'Toilet',      params: { width: 1.5, depth: 2.3 } },

  // ── Sink ──────────────────────────────────────────────────────────────────
  { id: 'sink-bath',   familyId: 'sink', domain: 'prop', label: 'Bathroom sink', params: { size: 'standard' } },
  { id: 'sink-kitchen',familyId: 'sink', domain: 'prop', label: 'Kitchen sink',  params: { size: 'large'    } },
  { id: 'sink-garden', familyId: 'sink', domain: 'prop', label: 'Garden sink',   params: { size: 'garden'   } },

  // ── Shower ────────────────────────────────────────────────────────────────
  { id: 'shower-std',  familyId: 'shower', domain: 'prop', label: 'Shower',      params: { width: 3, depth: 3 } },

  // ── Kitchen counter ───────────────────────────────────────────────────────
  { id: 'counter-std', familyId: 'kitchenCounter', domain: 'prop', label: 'Counter',  params: { width: 6, depth: 2 } },
  { id: 'counter-lg',  familyId: 'kitchenCounter', domain: 'prop', label: 'Counter (long)', params: { width: 10, depth: 2 } },

  // ── Car ───────────────────────────────────────────────────────────────────
  { id: 'car-hatch',   familyId: 'car', domain: 'prop', label: 'Hatchback',    params: { style: 'hatchback', width: 6, depth: 14 } },
  { id: 'car-suv',     familyId: 'car', domain: 'prop', label: 'SUV',          params: { style: 'suv',       width: 6.5, depth: 16 } },

  // ── Bike ──────────────────────────────────────────────────────────────────
  { id: 'bike-std',    familyId: 'bike', domain: 'prop', label: 'Bike',         params: { width: 2.5, depth: 6 } },
]

// ---------------------------------------------------------------------------
// Opening presets
// ---------------------------------------------------------------------------

export const OPENING_PRESETS: Preset[] = [
  // ── Door ──────────────────────────────────────────────────────────────────
  { id: 'door-internal', familyId: 'door', domain: 'opening', label: 'Internal door',
    params: { width: 3, height: 7, leaves: '1', panel: 'panel', swing: 'in', hinge: 'left' } },
  { id: 'door-main',     familyId: 'door', domain: 'opening', label: 'Main door (grand)',
    params: { width: 3.5, height: 7.5, leaves: '1', panel: 'glazed', swing: 'in', hinge: 'left' } },
  { id: 'door-double',   familyId: 'door', domain: 'opening', label: 'Double door',
    params: { width: 6, height: 7.5, leaves: '2', panel: 'glazed', swing: 'in', hinge: 'left' } },

  // ── Window ────────────────────────────────────────────────────────────────
  { id: 'window-std',    familyId: 'window', domain: 'opening', label: 'Standard window',
    params: { width: 4, height: 4, sillHeight: 3, style: 'plain' } },
  { id: 'window-large',  familyId: 'window', domain: 'opening', label: 'Large picture window',
    params: { width: 6, height: 5, sillHeight: 2, style: 'plain' } },
  { id: 'window-grand',  familyId: 'window', domain: 'opening', label: 'Grand window',
    params: { width: 5, height: 6, sillHeight: 1, style: 'grid' } },

  // ── Gate ──────────────────────────────────────────────────────────────────
  { id: 'gate-sliding',   familyId: 'gate', domain: 'opening', label: 'Sliding gate',
    params: { width: 10, height: 5, kinematics: 'sliding', leaves: '1', style: 'simple' } },
  { id: 'gate-biparting', familyId: 'gate', domain: 'opening', label: 'Bi-parting gate',
    params: { width: 12, height: 5, kinematics: 'biparting', leaves: '2', style: 'simple' } },
  { id: 'gate-swing',     familyId: 'gate', domain: 'opening', label: 'Swing gate',
    params: { width: 10, height: 5, kinematics: 'swing', leaves: '1', style: 'simple', swing: 'in', hinge: 'left' } },
]

// ---------------------------------------------------------------------------
// Indexes
// ---------------------------------------------------------------------------

export const ALL_PRESETS: Preset[] = [...PROP_PRESETS, ...OPENING_PRESETS]

export const PRESET_BY_ID: Record<string, Preset> = Object.fromEntries(
  ALL_PRESETS.map((p) => [p.id, p]),
)

export const PROP_PRESETS_BY_FAMILY: Record<string, Preset[]> = {}
for (const p of PROP_PRESETS) {
  ;(PROP_PRESETS_BY_FAMILY[p.familyId] ??= []).push(p)
}

export const OPENING_PRESETS_BY_FAMILY: Record<string, Preset[]> = {}
for (const p of OPENING_PRESETS) {
  ;(OPENING_PRESETS_BY_FAMILY[p.familyId] ??= []).push(p)
}
